import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/GamePage.css';
import '../styles/GISGameStyles.css';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import Preloader from './Preloader';
import { FaChevronLeft } from 'react-icons/fa';

import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from "../utils/numberFormat";
const API_URL = getApiUrl();

const GISGamePage = () => {
    const { gameUuid } = useParams();
    const [searchParams] = useSearchParams();
    const forcedDemo = searchParams.get('demo') === 'true';
    const gameName = searchParams.get('name');

    const navigate = useNavigate();
    const [gameUrl, setGameUrl] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [authLoading, setAuthLoading] = useState(true);
    const sessionCreated = useRef(false);
    const [sessionId, setSessionId] = useState('');
    const { isAuth, checkAuth } = useAuth();
    const { openPaymentModal, getCurrentBalance, selectedCurrency, isBalanceLoaded, isCurrencyLoaded } = usePayment();
    const [lobbyData, setLobbyData] = useState(null);
    const [needsLobby, setNeedsLobby] = useState(false);
    const [showLobbySelection, setShowLobbySelection] = useState(false);
    const [insufficientFunds, setInsufficientFunds] = useState(false);
    const [gameInfo, setGameInfo] = useState(null);
    const [currencyError, setCurrencyError] = useState(false);
    const [showDemoNotSupported, setShowDemoNotSupported] = useState(false);

    // Определяем демо режим: принудительно или если не авторизован
    const isDemo = forcedDemo || !isAuth;

    // Добавим панель управления для ПК версии
    const isMobile = window.innerWidth <= 768;



    // Проверяем авторизацию при загрузке
    useEffect(() => {
        const checkAuthStatus = async () => {
            console.log('🔐 GISGamePage: Проверка авторизации');
            await checkAuth();
            setAuthLoading(false);
            console.log('🔐 GISGamePage: Авторизация проверена, isAuth:', isAuth);
        };

        checkAuthStatus();
    }, []);

    const handleBack = async () => {
        console.log('🔙 GISGamePage: Возврат на главную');
        navigate('/');
    };

    const handleInsufficientFunds = () => {
        console.log('💰 GISGamePage: Недостаточно средств, открываем модал пополнения');
        setInsufficientFunds(false);
        openPaymentModal({ type: "deposit" });
    };

    const handleCurrencyError = () => {
        console.log('💱 GISGamePage: Ошибка валюты, предлагаем изменить валюту');
        setCurrencyError(false);
        // Можно открыть модал выбора валюты или перенаправить
        navigate('/');
    };

    const controlPanel = (
        <div className="control-panel">
            <div className="back-button-gamepage">
                <FaChevronLeft />
                <span onClick={handleBack}>Назад</span>
            </div>
            <span className="game-title">{gameName}</span>
        </div>
    );

    const [showPreloader, setShowPreloader] = useState(true);

    useEffect(() => {
        if (!isLoading) {
            const timer = setTimeout(() => setShowPreloader(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    // Получаем информацию об игре
    const fetchGameInfo = async () => {
        console.log('🎮 GISGamePage: Получение информации об игре', gameUuid);
        try {
            const response = await fetch(`${API_URL}/api/gis/games/${gameUuid}`);

            if (!response.ok) {
                throw new Error('Игра не найдена');
            }

            const data = await response.json();
            console.log('🎮 GISGamePage: Информация об игре получена:', data.game);
            setGameInfo(data.game);
            return data.game;
        } catch (err) {
            console.error('❌ GISGamePage: Ошибка при получении информации об игре:', err);
            throw err;
        }
    };

    // Проверяем, нужно ли лобби для игры (только если has_lobby === 1)
    const checkGameLobby = async () => {
        try {
            const response = await fetch(`${API_URL}/api/gis/games/lobby?game_uuid=${gameUuid}&currency=${selectedCurrency || 'RUB'}`);
            if (response.ok) {
                const lobbyInfo = await response.json();
                if (lobbyInfo.lobby && lobbyInfo.lobby.isOpen) {
                    // Если лобби найдено и открыт — возвращаем lobbyData
                    return lobbyInfo.lobby.lobbyData;
                }
            }
        } catch (err) {
            // Игнорируем ошибки — если лобби не найдено или закрыто, просто не передаем lobby_data
        }
        return null;
    };

    const initializeGame = async (selectedLobbyData = null) => {
        console.log('🎯 GISGamePage: Инициализация игры', {
            gameUuid,
            isDemo,
            selectedCurrency,
            selectedLobbyData
        });

        try {
            setIsLoading(true);
            let response;

            if (!isDemo) {
                console.log('🎮 GISGamePage: Запуск обычной игры');
                const requestBody = {
                    game_uuid: gameUuid,
                    currency: selectedCurrency || 'EUR',
                    return_url: getApiUrl(),
                    language: 'ru',
                    device: isMobile ? 'mobile' : 'desktop'
                };

                if (selectedLobbyData) {
                    requestBody.lobby_data = selectedLobbyData;
                }

                console.log('📤 GISGamePage: Отправка запроса на инициализацию игры:', requestBody);

                response = await fetch(`${API_URL}/api/gis/games/init`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(requestBody),
                });
            } else {
                console.log('🎮 GISGamePage: Запуск демо игры');
                const requestBody = {
                    game_uuid: gameUuid,
                    return_url: getApiUrl(),
                    language: 'ru',
                    device: isMobile ? 'mobile' : 'desktop'
                };

                console.log('📤 GISGamePage: Отправка запроса на инициализацию демо игры:', requestBody);

                response = await fetch(`${API_URL}/api/gis/games/init-demo`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody),
                });
            }

            console.log('📥 GISGamePage: Ответ от сервера:', response.status);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('❌ GISGamePage: Ошибка ответа сервера:', errorData);

                // Создаем объект ошибки с данными из ответа
                const error = new Error(errorData.message || 'Ошибка инициализации игры');
                error.code = errorData.code;
                error.suggestion = errorData.suggestion;
                throw error;
            }

            const data = await response.json();
            console.log('✅ GISGamePage: Игра успешно инициализирована:', data);

            if (!data.success || !data.url) {
                throw new Error('Неверный ответ сервера');
            }

            setGameUrl(data.url);
            setSessionId(data.session_id || '');
            console.log('🎮 GISGamePage: URL игры установлен:', data.url);
        } catch (err) {
            console.error('❌ GISGamePage: Ошибка при инициализации игры:', err);

            // Специальная обработка ошибок
            if (err.code === 'PROVIDER_NOT_ENABLED' && err.suggestion === 'CHANGE_CURRENCY') {
                console.log('💱 GISGamePage: Игра недоступна в текущей валюте:', selectedCurrency);
                setCurrencyError(true);
            } else if (err.code === 'DEMO_NOT_SUPPORTED' && err.suggestion === 'TRY_REAL_MODE') {
                console.log('🚫 GISGamePage: Игра не поддерживает демо-режим');
                setShowDemoNotSupported(true);
            } else if (err.code === 'DEMO_INIT_ERROR') {
                console.log('🚫 GISGamePage: Ошибка инициализации демо-режима');
                setError('Не удалось запустить демо-версию игры. Попробуйте другую игру.');
            } else if (err.code === 'PROVIDER_NOT_ENABLED') {
                setError('Эта игра временно недоступна. Попробуйте другую игру.');
            } else if (err.code === 'GAME_NOT_FOUND') {
                setError('Игра не найдена или недоступна.');
            } else if (err.message?.includes('Недостаточно средств')) {
                console.log('💰 GISGamePage: Недостаточно средств для игры');
                setInsufficientFunds(true);
            } else {
                setError('Не удалось запустить игру. Пожалуйста, попробуйте позже.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Ждем пока загрузится авторизация
        if (authLoading) {
            console.log('⏳ GISGamePage: Ожидание загрузки авторизации');
            return;
        }

        // Ждем пока загрузится PaymentContext (важно для получения правильной валюты)
        if (!isDemo) {
            if (!isBalanceLoaded || !isCurrencyLoaded) {
                console.log('⏳ GISGamePage: Ожидание загрузки PaymentContext/валюты');
                return;
            }
        }


        const launchGame = async () => {
            if (sessionCreated.current) {
                console.log('⚠️ GISGamePage: Сессия уже создана, пропускаем');
                return;
            }

            sessionCreated.current = true;
            console.log('🚀 GISGamePage: Запуск игры с валютой:', selectedCurrency);

            try {
                // Сначала получаем информацию об игре
                const game = await fetchGameInfo();

                // Проверяем, нужно ли лобби (только для авторизованных пользователей и игр с лобби)
                let lobbyData = null;
                if (!isDemo && game.has_lobby === 1) {
                    lobbyData = await checkGameLobby();
                }
                await initializeGame(lobbyData);
            } catch (err) {
                console.error('❌ GISGamePage: Ошибка при запуске игры:', err);
                setError('Игра не найдена или недоступна');
                setIsLoading(false);
            }
        };

        launchGame();

        // Обработчик сообщений от игры
        const handleGameMessage = (event) => {
            try {
                if (typeof event.data === 'string') {
                    const data = JSON.parse(event.data);
                    console.log('📨 GISGamePage: Сообщение от игры:', data);

                    if (data.event === 'close' || data.action === 'close') {
                        console.log('🔚 GISGamePage: Игра закрыта');
                        navigate('/');
                    }

                    if (data.event === 'insufficient_funds') {
                        console.log('💰 GISGamePage: Недостаточно средств (сообщение от игры)');
                        setInsufficientFunds(true);
                    }
                }
            } catch (err) {
                // Игнорируем ошибки парсинга
            }
        };

        window.addEventListener('message', handleGameMessage);

        return () => {
            window.removeEventListener('message', handleGameMessage);
        };
    }, [gameUuid, navigate, selectedCurrency, authLoading, isDemo, isBalanceLoaded, isCurrencyLoaded]);

    // Удалить полностью UI выбора стола и все связанные состояния/модалки

    // Функция для получения символа валюты
    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD':
                return '$';
            case 'RUB':
                return '₽';
            case 'BRL':
                return 'R$';
            case 'USDT':
                return '₮';
            case 'EUR':
                return '€';
            default:
                return '$';
        }
    };

    // Модал демо-режим недоступен
    if (showDemoNotSupported) {
        return (
            <div className="insufficient-funds-modal">
                <div className="modal-content">
                    <h2>Демо-режим недоступен</h2>
                    <p>Эта игра не поддерживает демо-режим. Войдите в аккаунт, чтобы играть на реальные деньги.</p>
                    <div className="modal-buttons">
                        <button onClick={() => navigate('/auth?mode=login')} className="btn deposit-btn">
                            Войти в аккаунт
                        </button>
                        <button onClick={handleBack} className="btn cancel-btn">
                            Выбрать другую игру
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Модал недостаточно средств
    if (insufficientFunds) {
        return (
            <div className="insufficient-funds-modal">
                <div className="modal-content">
                    <h2>Недостаточно средств</h2>
                    <p>У вас недостаточно средств для игры. Пополните баланс, чтобы продолжить.</p>
                    <div className="modal-buttons">
                        <button onClick={handleInsufficientFunds} className="btn deposit-btn">
                            Пополнить баланс
                        </button>
                        <button onClick={handleBack} className="btn cancel-btn">
                            Назад
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Модал ошибки валюты
    if (currencyError) {
        return (
            <div className="insufficient-funds-modal">
                <div className="modal-content">
                    <h2>Игра недоступна</h2>
                    <p>Эта игра недоступна в текущей валюте ({selectedCurrency}). Попробуйте изменить валюту в настройках профиля или выберите другую игру.</p>
                    <div className="modal-buttons">
                        <button onClick={handleCurrencyError} className="btn deposit-btn">
                            Перейти к играм
                        </button>
                        <button onClick={handleBack} className="btn cancel-btn">
                            Назад
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Отображение лобби для выбора стола
    // Удалить полностью UI выбора стола и все связанные состояния/модалки

    if (isLoading || authLoading || (!isDemo && (!isBalanceLoaded || !isCurrencyLoaded)) || !gameUrl) {
        return <Preloader isLoading={true} />;
    }

    if (error) {
        return (
            <div className="game-error">
                <h2>Ошибка запуска игры</h2>
                <p>{error}</p>
                <button onClick={handleBack} className="btn cancel-btn">
                    Назад
                </button>
            </div>
        );
    }

    return (
        <>
            {!isMobile && controlPanel}
            <div className="game-container">
                {gameUrl && (
                    <iframe
                        src={gameUrl}
                        title="GIS Игра"
                        className="game-frame"
                        allow="fullscreen"
                        onLoad={() => console.log('🎮 GISGamePage: Iframe загружен')}
                    />
                )}
                {isMobile && (
                    <div className="mobile-control-panel">
                        <div className="back-button-gamepage">
                            <FaChevronLeft />
                            <span onClick={handleBack}>Назад</span>
                        </div>
                        <div className="right-section">
                            <div className="header-balance">
                                <div className="header-balance-inner">
                                    <div className="balance-amount">
                                        {formatBalanceLocale(getCurrentBalance())}
                                    </div>
                                    <div className="currency-symbol">
                                        {getCurrencySymbol(selectedCurrency)}
                                    </div>
                                </div>
                            </div>
                            <button className="btn sign-up glow-blue" onClick={() => openPaymentModal({ type: "deposit" })}>Пополнить</button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default GISGamePage; 